// 通知收件匣（NOTIF-01；notify：GET /v1/notifications → {notifications, forced_list_version, next_cursor?}、POST /v1/notifications/{nid}/read|opened，handler 在各 plane 的 app-api）。
// 收件匣是「人」的：每個 plane 各一份（家戶在哪個 plane 就在哪一份），VMS 把目前看得到的 plane 合併、新到舊。
// 範圍切換只篩畫面（依 home_id），不改伺服器查詢——跨家戶的通知（帳號安全等沒有 home_id 的）一直都顯示。
import { kinioFetch, planeBase } from '@kinio/web-sdk';
import type { Plane } from './planeApi';

export interface InboxItem {
  nid: string;
  category: string;
  variant: string;
  home_id?: string;
  thing_id?: string;
  device_ref?: string;
  event_id?: string;
  title: string;
  body: string;
  /** 範本宣告的變數（去識別的警報只有 from_org_name） */
  vars?: Record<string, string>;
  link?: string;
  created_at: string;
  read_at?: string;
  opened_at?: string;
  /** 前端補：哪個 plane（標記已讀要打回同一個）。 */
  api: string;
}

export async function planeInbox(p: Plane, limit = 50): Promise<InboxItem[]> {
  const r = await kinioFetch<{ notifications?: Omit<InboxItem, 'api'>[] }>(planeBase(p.api) + `/v1/notifications?limit=${limit}`);
  return (r?.notifications ?? []).map((i) => ({ ...i, api: p.api }));
}

/** 多個 plane 的收件匣合併：依 created_at 新到舊、nid 去重。 */
export function mergeInbox(lists: (InboxItem[] | undefined)[]): InboxItem[] {
  const seen = new Set<string>();
  const out: InboxItem[] = [];
  for (const l of lists) {
    for (const i of l ?? []) {
      if (!seen.has(i.nid)) {
        seen.add(i.nid);
        out.push(i);
      }
    }
  }
  return out.sort((a, b) => (a.created_at < b.created_at ? 1 : a.created_at > b.created_at ? -1 : a.nid < b.nid ? 1 : -1));
}

/** 範圍篩選：沒有 home_id 的（帳號層）一直顯示；有的只留看得到的家戶。 */
export function inScope(items: InboxItem[], homeIds: Set<string>): InboxItem[] {
  return items.filter((i) => !i.home_id || homeIds.has(i.home_id));
}

export function unreadCount(items: InboxItem[]): number {
  return items.filter((i) => !i.read_at).length;
}

/**
 * 浮動通知（toast）跳不跳（SPEC UX-14、NOTIF-01，VMS UX 評審 2026-10-13 P0-1）：個人三檔、存在這台電腦。
 *   alarm（預設，建議）= 只有警報類；alarm_detect = 警報與一般偵測；none = 都不跳。只決定跳不跳，收件匣照常。
 */
export type ToastLevel = 'alarm' | 'alarm_detect' | 'none';
export const TOAST_LEVELS: ToastLevel[] = ['alarm', 'alarm_detect', 'none'];
const TOAST_LEVEL_KEY = 'kinio.vms.toastLevel';

/** 警報類：優先度 critical / time_sensitive 的人身與安全類（布防中的偵測與感測器觸發、門鈴、來電、布防模式、帳號安全）。 */
const ALARM_CATEGORIES = new Set(['security.alarm', 'security.alarm_task', 'device.doorbell', 'device.call', 'security.mode', 'account.security']);
/** 一般偵測（移動 / 人 / 車）：只在「警報與偵測」才跳。 */
const DETECT_CATEGORIES = new Set(['device.detection', 'security.detection']);

export function toastKind(category: string): 'alarm' | 'detect' | null {
  if (ALARM_CATEGORIES.has(category)) return 'alarm';
  if (DETECT_CATEGORIES.has(category)) return 'detect';
  return null;
}

export function toastAllowed(category: string, level: ToastLevel): boolean {
  const k = toastKind(category);
  if (level === 'none' || !k) return false;
  return k === 'alarm' || level === 'alarm_detect';
}

export function savedToastLevel(): ToastLevel {
  try {
    const v = localStorage.getItem(TOAST_LEVEL_KEY);
    return TOAST_LEVELS.includes(v as ToastLevel) ? (v as ToastLevel) : 'alarm';
  } catch {
    return 'alarm';
  }
}

export function saveToastLevel(v: ToastLevel): void {
  try {
    localStorage.setItem(TOAST_LEVEL_KEY, v);
  } catch {
    /* 無 storage */
  }
}

/** 這一輪新到、要跳 toast 的：比上一輪看過的新（created_at > since）、未讀、還沒 toast 過、類別在這台電腦的浮動通知設定內。 */
export function freshForToast(items: InboxItem[], since: string, toasted: Set<string>, level: ToastLevel = 'alarm'): InboxItem[] {
  // 用時間值比，不用字串比（「03:47:00Z」跟「03:47:00.000Z」字串比會判錯，第四輪 B4-01）
  const s = Date.parse(since) || 0;
  return items.filter((i) => (Date.parse(i.created_at) || 0) > s && !i.read_at && !toasted.has(i.nid) && toastAllowed(i.category, level));
}

/** 通知裡的設備完整名（NVR 通道寫「NVR633 / CH3」）；找不到回空字串。 */
/**
 * 收件匣標題（UX-14 ⑤，第二輪 B2-29）：拿掉 notify 在 dev / test 加的「[dev]」前綴（環境另有標籤）；
 * 標題只寫通道短名（「CH4」）而找得到完整設備名時，用完整名「NVR631 / CH4」。
 */
export function inboxTitle(title: string | undefined, fullDeviceName: string): string {
  const clean = (title ?? '').replace(/^\s*\[(dev|test|staging)\]\s*/i, '');
  if (fullDeviceName && clean && clean !== fullDeviceName && fullDeviceName.endsWith(' / ' + clean)) return fullDeviceName;
  return clean;
}

export function inboxDeviceName(
  devices: { home_id: string; device_ref: string; display_name?: string; children?: { device_ref: string; display_name?: string }[] }[],
  i: Pick<InboxItem, 'home_id' | 'device_ref'>,
): string {
  if (!i.home_id || !i.device_ref) return '';
  for (const d of devices) {
    if (d.home_id !== i.home_id) continue;
    if (d.device_ref === i.device_ref) return d.display_name || '';
    const c = (d.children ?? []).find((x) => x.device_ref === i.device_ref);
    if (c) return [d.display_name, c.display_name].filter(Boolean).join(' / ');
  }
  return '';
}

export function markInbox(api: string, nid: string, what: 'read' | 'opened'): Promise<unknown> {
  return kinioFetch(planeBase(api) + `/v1/notifications/${encodeURIComponent(nid)}/${what}`, { method: 'POST' });
}

/** 點通知要去哪：有事件 → 事件詳細；有設備 → 設備頁；其他留在原地（notify 的 link 是 App 的路徑，VMS 不直接用）。 */
/** 去識別的警報（委派方要求兩步驟驗證，SPEC #org-tree-ui，Eddy 2026-10-09 選單 A）的 variant */
export const VARIANT_DELEGATOR_MFA = 'delegator_mfa_required';

export function inboxTarget(i: InboxItem): string | null {
  if (i.variant === VARIANT_DELEGATOR_MFA) {
    // 不直接開事件（會 403 ORG_MFA_REQUIRED）：先到說明頁，設好兩步驟驗證、重新登入後再回到那則警報
    const q = new URLSearchParams({ from: i.vars?.from_org_name ?? '' });
    if (i.home_id && i.event_id) {
      q.set('home', i.home_id);
      q.set('event', i.event_id);
    }
    return `/alerts/mfa?${q}`;
  }
  // 警報指派 / 超過接手時限（EVT-07）：到警報工作台的那一則；同一輪多則（overdue_many，沒有事件）到工作台
  if (i.category === 'security.alarm_task') return i.home_id && i.event_id ? `/events?tab=alarms&sel=${encodeURIComponent(`${i.home_id}/${i.event_id}`)}` : '/events?tab=alarms';
  if (i.home_id && i.event_id) return `/events/${encodeURIComponent(i.home_id)}/${encodeURIComponent(i.event_id)}`;
  if (i.home_id && i.device_ref) return `/devices/${encodeURIComponent(i.home_id)}/${encodeURIComponent(i.device_ref)}`;
  return null;
}
